import { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useBroadcastNotification } from '../../../hooks/useNotifications';

export function BroadcastForm() {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');

  const { mutate: broadcast, isPending, isSuccess, data, reset } = useBroadcastNotification();

  useEffect(() => {
    if (!isSuccess) return;
    setTitle('');
    setMessage('');
    const timer = setTimeout(reset, 4000);
    return () => clearTimeout(timer);
  }, [isSuccess, reset]);

  const isDisabled = isPending || !title.trim() || !message.trim();

  const handleSend = () => {
    if (isDisabled) return;
    broadcast({ title: title.trim(), message: message.trim() });
  };

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        padding: '1.25rem 1.5rem',
        marginBottom: '1.25rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#3b82f6' }}>
          campaign
        </span>
        <span style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>
          {t('dashboard.notifications.broadcastSendHeading')}
        </span>
      </div>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('dashboard.notifications.broadcastTitlePlaceholder')}
        maxLength={100}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          fontSize: 13,
          color: '#0f172a',
          outline: 'none',
          marginBottom: '0.625rem',
          boxSizing: 'border-box',
        }}
      />

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t('dashboard.notifications.broadcastMessage')}
        maxLength={1000}
        rows={3}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          fontSize: 13,
          color: '#0f172a',
          outline: 'none',
          resize: 'vertical',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '0.75rem',
        }}
      >
        {isSuccess ? (
          <span
            style={{
              fontSize: 13,
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
              check_circle
            </span>
            {t('dashboard.notifications.broadcastSentTo', { count: data?.sent ?? 0 })}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{message.length}/1000</span>
        )}

        <button
          onClick={handleSend}
          disabled={isDisabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.5rem 1rem',
            background: isDisabled ? '#e2e8f0' : '#3b82f6',
            border: 'none',
            borderRadius: 8,
            color: isDisabled ? '#94a3b8' : '#fff',
            fontSize: 13,
            fontWeight: 500,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          {isPending ? (
            <CircularProgress size={14} style={{ color: '#94a3b8' }} />
          ) : (
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
              send
            </span>
          )}
          {t('dashboard.notifications.broadcastSendToAll')}
        </button>
      </div>
    </div>
  );
}
